import { useParams, Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

interface Article {
  slug: string;
  title: string;
  category: string;
  date: string;
  author: string;
  readTime: string;
  image: string;
  content: {
    introduction: string;
    sections: {
      heading: string;
      content: string;
    }[];
    conclusion: string;
  };
}

const articles: Record<string, Article> = {
  'smart-manufacturing-trends-implementation': {
    slug: 'smart-manufacturing-trends-implementation',
    title: 'Smart Manufacturing Trends and Implementation',
    category: 'Technology',
    date: 'June 17, 2025',
    author: 'HI-TAK Engineering Team',
    readTime: '8 min read',
    image: 'https://public.readdy.ai/ai/img_res/02926596f721c1f253cc59c42a65a38f.jpg',
    content: {
      introduction: 'The manufacturing industry is undergoing a profound transformation driven by digital technologies, automation, and data analytics. At HI-TAK, we are at the forefront of this revolution, integrating smart manufacturing principles into our precision gun drilling systems to deliver unprecedented levels of efficiency, quality, and flexibility.',
      sections: [
        {
          heading: 'The Evolution of Smart Manufacturing',
          content: 'Smart manufacturing represents the convergence of physical production systems with digital technologies. This integration enables real-time monitoring, predictive maintenance, and adaptive manufacturing processes that respond dynamically to changing conditions. Our latest gun drilling systems incorporate IoT sensors, machine learning algorithms, and cloud connectivity to provide manufacturers with complete visibility and control over their deep hole drilling operations.'
        },
        {
          heading: 'Key Technologies Driving Change',
          content: 'Several core technologies are reshaping the manufacturing landscape. Industrial IoT enables continuous data collection from machines, providing insights into performance, tool wear, and process optimization. Artificial intelligence and machine learning analyze this data to predict maintenance needs, optimize cutting parameters, and improve overall equipment effectiveness. Digital twins create virtual replicas of physical systems, allowing for simulation and optimization before implementation.'
        },
        {
          heading: 'HI-TAK\'s Smart Manufacturing Solutions',
          content: 'Our gun drilling systems now feature advanced monitoring capabilities that track critical parameters such as spindle vibration, cutting force, coolant pressure, and tool wear in real-time. This data is processed using proprietary algorithms to automatically adjust machining parameters, ensuring optimal performance and extending tool life. The systems can also communicate with enterprise resource planning (ERP) and manufacturing execution systems (MES) for seamless integration into smart factory environments.'
        },
        {
          heading: 'Benefits for Manufacturers',
          content: 'Implementing smart manufacturing technologies delivers tangible benefits across multiple dimensions. Productivity increases through reduced downtime and optimized cycle times. Quality improves through consistent process control and early detection of anomalies. Costs decrease through predictive maintenance, reduced scrap, and efficient resource utilization. Flexibility enhances as systems adapt quickly to new products and changing requirements.'
        },
        {
          heading: 'Implementation Strategy',
          content: 'Successfully adopting smart manufacturing requires a strategic approach. We work closely with customers to assess their current capabilities, identify improvement opportunities, and develop phased implementation plans. This includes infrastructure upgrades, operator training, and integration with existing systems. Our comprehensive support ensures smooth transitions and rapid realization of benefits.'
        }
      ],
      conclusion: 'Smart manufacturing is not just a trend—it is the future of precision machining. HI-TAK is committed to leading this transformation, providing our customers with the technologies, expertise, and support needed to thrive in the digital age. Contact us to learn how our smart gun drilling solutions can transform your manufacturing operations.'
    }
  },
  'gun-drilling-ld400-breakthrough': {
    slug: 'gun-drilling-ld400-breakthrough',
    title: 'New Gun Drilling System Achieves L/D400 Breakthrough',
    category: 'Product Launch',
    date: 'May 28, 2025',
    author: 'HI-TAK Product Development',
    readTime: '6 min read',
    image: 'https://readdy.ai/api/search-image?query=Precision%20gun%20drilling%20machine%20with%20advanced%20spindle%20system%20drilling%20deep%20holes%20in%20metal%20workpiece%20with%20coolant%20flow%20in%20modern%20manufacturing%20facility%20with%20teal%20accent%20lighting&width=1200&height=600&seq=newsdetail2&orientation=landscape',
    content: {
      introduction: 'HI-TAK is proud to announce a groundbreaking achievement in deep hole drilling technology. Our new gun drilling system has successfully achieved L/D400 ratios with exceptional precision and repeatability, setting a new industry standard for deep hole machining capabilities.',
      sections: [
        {
          heading: 'Technical Innovation',
          content: 'Achieving L/D400 required fundamental innovations in multiple areas. Our proprietary hollow spindle motor design minimizes vibration even at extreme depths. Advanced coolant delivery systems maintain optimal pressure and flow throughout the drilling process. Precision guide bushings and tool support mechanisms ensure straightness and concentricity over unprecedented lengths.'
        },
        {
          heading: 'Applications and Benefits',
          content: 'This breakthrough opens new possibilities for manufacturers across industries. Medical device companies can produce longer surgical instruments with internal channels. Aerospace manufacturers can drill deeper cooling passages in turbine components. Automotive suppliers can create more efficient fuel injection systems. The ability to achieve L/D400 eliminates the need for multiple setups and secondary operations, reducing costs and improving quality.'
        },
        {
          heading: 'Proven Performance',
          content: 'Extensive testing has validated the system\'s capabilities across various materials including stainless steel, titanium, and hardened alloys. Hole straightness remains within 0.1mm over 400mm depths. Surface finish consistently achieves Ra 0.8μm or better. Tool life has been optimized through advanced coating technologies and cutting parameter optimization.'
        }
      ],
      conclusion: 'This L/D400 breakthrough represents years of research, development, and testing. It demonstrates HI-TAK\'s commitment to pushing the boundaries of what is possible in precision deep hole drilling. We invite you to experience this revolutionary technology and discover how it can transform your manufacturing capabilities.'
    }
  },
  'medical-device-partnership': {
    slug: 'medical-device-partnership',
    title: 'Partnership with Leading Medical Device Manufacturer',
    category: 'Business',
    date: 'May 15, 2025',
    author: 'HI-TAK Business Development',
    readTime: '5 min read',
    image: 'https://readdy.ai/api/search-image?query=Medical%20device%20manufacturing%20facility%20with%20precision%20surgical%20instruments%20and%20implants%20being%20machined%20on%20advanced%20equipment%20in%20sterile%20clean%20room%20environment%20with%20white%20and%20teal%20lighting&width=1200&height=600&seq=newsdetail3&orientation=landscape',
    content: {
      introduction: 'HI-TAK has entered into a strategic partnership with a leading global medical device manufacturer to provide advanced gun drilling solutions for next-generation surgical instruments and implants. This collaboration strengthens our position as the preferred supplier for precision deep hole drilling in the medical industry.',
      sections: [
        {
          heading: 'Meeting Medical Industry Standards',
          content: 'The medical device industry demands the highest levels of precision, cleanliness, and traceability. Our gun drilling systems are specifically configured to meet these stringent requirements. Clean room compatible designs, validated processes, and comprehensive documentation ensure compliance with FDA and ISO 13485 standards. Every hole is traceable with complete process data recording.'
        },
        {
          heading: 'Advanced Surgical Instruments',
          content: 'Modern surgical instruments require complex internal geometries for fluid delivery, suction, and visualization. Our small diameter gun drilling capabilities enable the production of instruments with holes as small as 0.5mm diameter extending through long, curved paths. This precision enables minimally invasive procedures that improve patient outcomes and reduce recovery times.'
        },
        {
          heading: 'Implant Manufacturing',
          content: 'Orthopedic implants increasingly incorporate internal channels for bone growth promotion and drug delivery. Our gun drilling systems produce these channels with the precision and surface finish required for biocompatibility and long-term performance. We work closely with implant designers to optimize hole geometries for both manufacturing efficiency and clinical effectiveness.'
        }
      ],
      conclusion: 'This partnership validates HI-TAK\'s expertise in medical device manufacturing and our commitment to supporting healthcare innovation. We look forward to contributing to the development of medical technologies that improve and save lives around the world.'
    }
  }
};

export default function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 px-4 text-center">
          <h1 className="text-4xl font-bold text-[#001E3C] mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 bg-[#00D9C9] text-[#001E3C] px-6 py-3 rounded-xl font-bold hover:bg-[#00F5E0] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
            Back to News
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Image */}
      <div className="pt-16">
        <div className="relative h-[500px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-[#00D9C9] transition-colors duration-200">
              Home
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/news" className="hover:text-[#00D9C9] transition-colors duration-200">
              News
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-700">{article.category}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-4 py-2 bg-[#00D9C9]/10 text-[#00D9C9] rounded-full text-sm font-semibold">
                {article.category}
              </span>
              <span className="text-gray-500 text-sm">{article.date}</span>
              <span className="text-gray-500 text-sm">•</span>
              <span className="text-gray-500 text-sm">{article.readTime}</span>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-[#001E3C] mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-gray-600">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00D9C9] to-[#00F5E0] rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#001E3C]">{article.author}</p>
                  <p className="text-sm text-gray-500">HI-TAK Corporation</p>
                </div>
              </div>
            </div>
          </header>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-10 font-medium">
              {article.content.introduction}
            </p>

            {article.content.sections.map((section, index) => (
              <div key={index} className="mb-10">
                <h2 className="text-3xl font-bold text-[#001E3C] mb-4">
                  {section.heading}
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {section.content}
                </p>
              </div>
            ))}

            <div className="mt-12 p-8 bg-gradient-to-br from-[#00D9C9]/5 to-[#00F5E0]/5 rounded-2xl border-l-4 border-[#00D9C9]">
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                {article.content.conclusion}
              </p>
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-6">
              <div>
                <h3 className="text-lg font-bold text-[#001E3C] mb-2">Share this article</h3>
                <div className="flex items-center gap-3">
                  <button className="w-10 h-10 bg-gray-100 hover:bg-[#00D9C9] text-gray-600 hover:text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer">
                    <i className="ri-linkedin-fill text-xl"></i>
                  </button>
                  <button className="w-10 h-10 bg-gray-100 hover:bg-[#00D9C9] text-gray-600 hover:text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer">
                    <i className="ri-twitter-x-fill text-xl"></i>
                  </button>
                  <button className="w-10 h-10 bg-gray-100 hover:bg-[#00D9C9] text-gray-600 hover:text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer">
                    <i className="ri-facebook-fill text-xl"></i>
                  </button>
                  <button className="w-10 h-10 bg-gray-100 hover:bg-[#00D9C9] text-gray-600 hover:text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer">
                    <i className="ri-mail-fill text-xl"></i>
                  </button>
                </div>
              </div>
              
              <Link
                to="/news"
                className="inline-flex items-center gap-2 bg-[#00D9C9] text-[#001E3C] px-6 py-3 rounded-xl font-bold hover:bg-[#00F5E0] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Back to News
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#001E3C] mb-12 text-center">
            Related Articles
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {Object.values(articles).filter(a => a.slug !== article.slug).slice(0, 3).map((relatedArticle) => (
              <Link
                key={relatedArticle.slug}
                to={`/news/${relatedArticle.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200"
              >
                <div className="relative h-[200px] overflow-hidden">
                  <img
                    src={relatedArticle.image}
                    alt={relatedArticle.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-[#00D9C9]/10 text-[#00D9C9] rounded-full text-xs font-semibold">
                      {relatedArticle.category}
                    </span>
                    <span className="text-gray-500 text-xs">{relatedArticle.date}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#001E3C] group-hover:text-[#00D9C9] transition-colors duration-300">
                    {relatedArticle.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
